// Talks to the worker. Errors carry the server's plain-English message.

import { navigate } from './router';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public data: Record<string, unknown>,
  ) {
    super(message);
  }
}

const PUBLIC = ['/api/login', '/api/setup', '/api/health'];

export async function api<T = Record<string, unknown>>(path: string, opts: { method?: string; body?: unknown; form?: FormData } = {}): Promise<T> {
  const method = opts.method || (opts.body !== undefined || opts.form ? 'POST' : 'GET');
  const headers: Record<string, string> = { 'x-requested-with': 'lavir' };
  let payload: BodyInit | undefined;
  if (opts.form) payload = opts.form;
  else if (opts.body !== undefined) {
    headers['content-type'] = 'application/json';
    payload = JSON.stringify(opts.body);
  } else if (method !== 'GET') {
    headers['content-type'] = 'application/json';
    payload = '{}';
  }
  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: payload, credentials: 'same-origin' });
  } catch {
    throw new ApiError(0, 'No connection. Check your internet and try again.', {});
  }
  let data: Record<string, unknown> = {};
  try {
    data = await res.json();
  } catch {
    /* not JSON */
  }
  if (res.status === 401 && !PUBLIC.includes(path)) {
    const here = location.pathname + location.search;
    // Skips the leave-guards: the editor keeps unsaved work on this device and puts it back after sign-in.
    if (location.pathname !== '/login') navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true, force: true });
    throw new ApiError(401, 'Please sign in.', data);
  }
  if (!res.ok) throw new ApiError(res.status, String(data.error || `Something went wrong (${res.status}).`), data);
  return data as T;
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}
